/**
 * Racine d'un salon : aiguille sur l'ecran de la phase courante.
 *
 * Un lien /room/CODE ouvert sans pseudo affiche d'abord une saisie, puis
 * rejoint automatiquement. Le snapshot serveur reste la seule source de verite :
 * cet ecran ne decide jamais d'une phase par lui-meme.
 */
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { sanitizeNickname } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { leaveRoom } from '../net/leaveRoom.js';
import { errorKey, useRoomStore } from '../store/roomStore.js';
import { useUiStore } from '../store/uiStore.js';
import { LobbyScreen } from './LobbyScreen.js';
import { CountdownScreen } from './CountdownScreen.js';
import { DrawingScreen } from './DrawingScreen.js';
import { GuessingScreen } from './GuessingScreen.js';
import { RevealScreen } from './RevealScreen.js';
import { GameEndScreen } from './GameEndScreen.js';
import { Screen } from '../ui/Screen.js';
import { navigate } from '../router.js';

/** Delai au-dela duquel un salon qui ne repond pas est traite comme un echec. */
const JOIN_TIMEOUT_MS = 8000;

export function RoomScreen({ code }: { code: string }): ReactElement {
  const t = useT();
  const room = useRoomStore((state) => state.room);
  const status = useRoomStore((state) => state.status);
  const nickname = useUiStore((state) => state.nickname);
  const setNickname = useUiStore((state) => state.setNickname);

  const joinError = useRoomStore((state) => state.joinError);

  const [draft, setDraft] = useState(nickname);
  const [needsName, setNeedsName] = useState(sanitizeNickname(nickname) === null);
  /** Le serveur ne repond pas : on ne laisse pas le joueur devant des points. */
  const [timedOut, setTimedOut] = useState(false);

  // Filet : sans reponse au bout de JOIN_TIMEOUT_MS, on propose une sortie.
  useEffect(() => {
    if (needsName || room !== null || joinError !== null) {
      setTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setTimedOut(true), JOIN_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [needsName, room, joinError]);

  const retry = useCallback(() => {
    const clean = sanitizeNickname(nickname);
    if (clean === null) {
      setNeedsName(true);
      return;
    }
    useRoomStore.setState({ joinError: null });
    setTimedOut(false);
    // `rejoin` et non `join` : la tentative precedente a echoue, le serveur ne
    // l'a pas retenue, la meme intention doit pouvoir repartir a l'identique.
    connection.join(clean, { code });
    connection.rejoin();
  }, [code, nickname]);

  // Arrivee directe par l'URL : on rejoint des qu'on a un pseudo utilisable.
  useEffect(() => {
    if (needsName) return;
    if (room?.code === code) return;
    const clean = sanitizeNickname(nickname);
    if (clean === null) {
      setNeedsName(true);
      return;
    }
    connection.join(clean, { code });
  }, [code, needsName, nickname, room?.code]);

  const confirmName = useCallback(() => {
    const clean = sanitizeNickname(draft);
    if (clean === null) return;
    setNickname(clean);
    setNeedsName(false);
  }, [draft, setNickname]);

  if (needsName) {
    return (
      <Screen name="join-overlay">
        <main className="home">
          <header className="home__header">
            <h1 className="home__title">{t('app.title')}</h1>
            <p className="home__tagline mono-tabular">{code}</p>
          </header>
          <div className="home__form">
            <label className="field">
              <span className="field__label">{t('home.nickname')}</span>
              <input
                className="field__input"
                value={draft}
                maxLength={24}
                autoFocus
                placeholder={t('home.nicknamePlaceholder')}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') confirmName();
                }}
              />
            </label>
            <button
              type="button"
              className="button-primary button-block"
              disabled={sanitizeNickname(draft) === null}
              onClick={confirmName}
            >
              {t('home.join')}
            </button>
          </div>
        </main>
      </Screen>
    );
  }

  // Entree refusee, ou serveur muet : un ecran qui EXPLIQUE et qui propose une
  // sortie. Rester sur les points de suspension enfermait le joueur dans un
  // cul-de-sac, sans meme un retour a l'accueil.
  if (room === null && (joinError !== null || timedOut)) {
    return (
      <Screen name="join-failed">
        <main className="home">
          <header className="home__header">
            <h1 className="home__title">{t('join.failedTitle')}</h1>
            <p className="home__tagline">
              {joinError === null ? t('join.noAnswer') : t(errorKey(joinError))}
            </p>
            <p className="lobby__label mono-tabular">{code}</p>
          </header>

          <div className="home__form">
            <button type="button" className="button-primary button-block" onClick={retry}>
              {t('join.retry')}
            </button>
            <button
              type="button"
              className="button-ghost button-block"
              onClick={() => navigate('/browse')}
            >
              {t('home.browse')}
            </button>
            <button
              type="button"
              className="button-ghost button-block"
              onClick={() => {
                // L'entree a ete refusee : il n'y a pas de place a rendre.
                leaveRoom({ notify: false });
                navigate('/');
              }}
            >
              {t('join.home')}
            </button>
          </div>
        </main>
      </Screen>
    );
  }

  if (room === null) {
    return (
      <Screen name="joining">
        <p className="sandbox__status">
          {status === 'reconnecting' ? t('net.reconnecting') : t('join.connecting')}
        </p>
      </Screen>
    );
  }

  switch (room.phase) {
    case 'LOBBY':
      return <LobbyScreen room={room} />;
    case 'COUNTDOWN':
      return <CountdownScreen endsAt={room.endsAt} />;
    case 'DRAWING':
    case 'COLLECTING':
      return <DrawingScreen room={room} />;
    case 'GUESSING':
      return <GuessingScreen room={room} />;
    case 'REVEAL':
      return <RevealScreen room={room} />;
    case 'GAME_END':
      return <GameEndScreen room={room} />;
  }
}
